import React, { useState } from "react"
import types from "prop-types"
import Formsy from "formsy-react"
import { isEqual, isNil } from "lodash-es"
import { useSelector } from "react-redux"
import { submitterFullName as submitterFullNameSelector } from "reduxSlices/formSubmissionSlice"
import { titles } from "reduxSlices/formSubmissionConversationsSlice"
import { canonicalTitle } from "utils/conversationHelpers"
import { PrimaryButton, TertiaryButton } from "shared/buttons"
import FieldLabel from "shared/FieldLabel"
import { FormsyMultilineTextInput, FormsyTextInput } from "shared/inputs"

const CONVERSATION_FORM_ID = "conversation-form"

const ConversationForm = ({
  initialValues = {}, onCancel, onSubmit, ref = undefined,
}) => {
  const [formIsValid, setFormIsValid] = useState(false)

  const submitterName = useSelector(submitterFullNameSelector)
  const otherConversationTitles = useSelector(titles, isEqual)

  const disableSubmit = () => setFormIsValid(false)
  const enableSubmit = () => setFormIsValid(true)

  const { title } = initialValues

  return (
    <Formsy
      id={CONVERSATION_FORM_ID}
      onInvalid={disableSubmit}
      onSubmit={onSubmit}
      onValid={enableSubmit}
    >
      <div className="flex flex-col gap-8">
        <FieldLabel className="w-full !max-w-full" text="Title">
          <FormsyTextInput
            className="bordered-base-input text-sm"
            inputRef={ref}
            name="title"
            placeholder="Conversation Title"
            required
            validations={{
              isNotBlankString: true,
              noExcelMetacharacters: true,
              noHTML: true,
              noRepeat: {
                canonize: canonicalTitle,
                errorMessage: "Cannot repeat conversation titles",
                otherValues: otherConversationTitles,
              },
            }}
            validationErrors={{ isDefaultRequiredValue: "required" }}
            value={title}
          />
        </FieldLabel>
        <div>
          <FieldLabel className="w-full !max-w-full" text="Message">
            <FormsyMultilineTextInput
              className="bordered-base-input text-sm h-32 overflow-y-auto thin-scrollbar transparent-scroll-track"
              name="messageText"
              placeholder="Message ..."
              required
              validations={{
                isNotBlankString: true,
                noExcelMetacharacters: true,
                noHTML: true,
              }}
              validationErrors={{ isDefaultRequiredValue: "required" }}
            />
          </FieldLabel>
          <div className="flex flex-col gap-4 text-xs text-dark">
            <p>
              This conversation will be accessible to the submitter
              {isNil(submitterName) ? "" : `, ${submitterName},`} until the submission is closed.
              It will be permanently accessible to any user with access to this submission.
            </p>
            <p>
              Conversations cannot be deleted or edited.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2">
          <TertiaryButton
            ariaLabel="cancel"
            onClick={onCancel}
            text="Cancel"
          />
          <PrimaryButton
            ariaLabel="submit conversation form"
            disabled={!formIsValid}
            form={CONVERSATION_FORM_ID}
            text="Submit"
            type="submit"
          />
        </div>
      </div>
    </Formsy>
  )
}

ConversationForm.displayName = "ConversationForm"

ConversationForm.propTypes = {
  initialValues: types.object,
  onCancel: types.func.isRequired,
  onSubmit: types.func.isRequired,
  ref: types.oneOfType([types.func, types.object]),
}

export default ConversationForm
